import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  getBranchesPaginated,
  getBranches, 
  getBranchById, 
  createBranch, 
  updateBranch, 
  deleteBranch, 
  BranchApiResponse
} from "../../services/branchService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Branch from "../../types/branch";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";

interface BranchContextProps {
  apiResponse: ApiResponse<Branch> | null;
  branches: Branch[] | null;
  branchById: Branch | null;
  loading: boolean;
  error: string | null;
  fetchBranches: (paginacao: Paginacao | null) => void;
  fetchBranchById: (id: number) => Promise<Branch | null>;
  postBranch: (branch: Branch) => Promise<BranchApiResponse | null>;
  putBranch: (id: number, branch: Branch) => Promise<BranchApiResponse | null>;
  excluiBranch: (id: number) => Promise<boolean>;
}

interface BranchProviderProps {
  children: React.ReactNode;
}

const BranchContext = createContext<BranchContextProps | null>(null);

const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<Branch> | null>(null);
  const [branches, setBranch] = useState<Branch[] | null>(null);
  const [branchById, setBranchById] = useState<Branch | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBranches = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getBranchesPaginated(paginacao) : await getBranches();
      setApiResponse(response);
      setBranch(response.data.values || []);
    } catch (err) {
      setBranch([]);
      setError(getErrorMessage("fetchBranchError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranchById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getBranchById(id);
      
      setBranchById(response.data);
      return response.data;
    } catch (err) {
      setError(getErrorMessage("fetchBranchByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postBranch = useCallback(async (branch: Branch) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createBranch(branch);
      return response;
    } catch (err) {
      setError(getErrorMessage("createBranchError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const putBranch = useCallback(async (id: number, branch: Branch) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateBranch(id, branch);
      return response;
    } catch (err) {
      setError(getErrorMessage("updateBranchError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const excluiBranch = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteBranch(id);
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteBranchError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <BranchContext.Provider 
      value={{ 
        apiResponse,
        branches, 
        branchById, 
        loading, 
        error, 
        fetchBranches, 
        fetchBranchById, 
        postBranch, 
        putBranch, 
        excluiBranch 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </BranchContext.Provider>
  );
};

export default BranchProvider;

export const useBranch = (): BranchContextProps => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useBranch?.name,
        providerName: BranchProvider?.name,
      })
    );
  }
  return context;
};
